import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../../util/log';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';

import { dailySeed, pickFeaturedProducts } from './sections/featuredProducts';

// How many of the newest listings to look through when choosing the "Featured
// products" (and filling the category rows and brands). A deep pool gives the
// featured row room to mix vendors and categories; 100 is the most Sharetribe
// returns in one page. The page looks intentional whatever comes back, so this
// is only an upper bound.
const RESULT_PAGE_SIZE = 100;
// How many of the newest listings the hero looks through to find products from
// four different vendors; the featured grid's 12 can all be from one vendor.
const HERO_POOL_SIZE = 48;

// The listing fields and image variants every homepage section needs.
const listingQueryParams = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    listingImageConfig || {};
  const aspectRatio = aspectHeight / aspectWidth;

  return {
    // The author's profile image is the brand logo in "Featured brands". It has
    // to be included here: without it the logo only appeared after visiting a
    // brand's profile page (which loads the image into the shared store).
    include: ['images', 'author', 'author.profileImage'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      // Request the whole publicData object (not a fixed list of sub-keys) so
      // that the ecolabels a vendor has ticked reach the listing cards. The
      // ecolabel field key lives in the hosted Sharetribe config, so we can't
      // name it here — fetching all of publicData keeps the cards in sync with
      // whatever key is used, while still including listingType/cardStyle/etc.
      'publicData',
    ],
    'fields.image': [
      'variants.listing-card',
      'variants.listing-card-2x',
      'variants.scaled-small',
      'variants.scaled-medium',
      // Avatar variants for the brand logos.
      'variants.square-xsmall',
      'variants.square-xsmall2x',
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };
};

// ================ Async thunks ================ //

// Fetch the newest published listings straight from the Marketplace API and
// choose the featured products from them. Nothing is hardcoded here: whatever
// Sharetribe returns is what the homepage renders, so the page scales naturally
// as the catalogue grows. The featured pick is made here (rather than while
// rendering) so the server-rendered page and the browser show the same row.
export const fetchNewestListings = createAsyncThunk(
  'LandingPage/fetchNewestListings',
  async (arg, thunkAPI) => {
    const { extra: sdk, rejectWithValue, dispatch } = thunkAPI;
    const { listingImageConfig, categoryKey } = arg || {};

    try {
      const response = await sdk.listings.query({
        perPage: RESULT_PAGE_SIZE,
        page: 1,
        minStock: 1,
        stockMode: 'match-undefined',
        ...listingQueryParams(listingImageConfig),
      });

      dispatch(addMarketplaceEntities(response));
      const listings = denormalisedResponseEntities(response);
      const featured = pickFeaturedProducts(listings, { categoryKey, seed: dailySeed() });
      return {
        listingIds: listings.map(listing => listing.id),
        featuredListingIds: featured.map(listing => listing.id),
      };
    } catch (error) {
      log.error(error, 'landing-page-listings-fetch-failed');
      return rejectWithValue(storableError(error));
    }
  }
);

// Fetch a deeper pool of the newest listings for the hero, which shows one
// product per vendor. A failure isn't shown to the shopper: the hero falls back
// to the listings fetched for the featured grid.
export const fetchHeroListings = createAsyncThunk(
  'LandingPage/fetchHeroListings',
  async (arg, thunkAPI) => {
    const { extra: sdk, rejectWithValue, dispatch } = thunkAPI;
    const { listingImageConfig } = arg || {};

    try {
      const response = await sdk.listings.query({
        perPage: HERO_POOL_SIZE,
        page: 1,
        minStock: 1,
        stockMode: 'match-undefined',
        ...listingQueryParams(listingImageConfig),
      });

      dispatch(addMarketplaceEntities(response));
      return { listingIds: response.data.data.map(listing => listing.id) };
    } catch (error) {
      log.error(error, 'landing-page-hero-listings-fetch-failed');
      return rejectWithValue(storableError(error));
    }
  }
);

// Count the published listings filed under each top-level category, so the
// homepage can hide categories that don't have any products yet. Each count is
// a one-result query that only reads `meta.totalItems`. A category whose count
// can't be fetched is left out of the result (unknown), and the page falls back
// to the listings it already has for that category.
export const fetchCategoryCounts = createAsyncThunk(
  'LandingPage/fetchCategoryCounts',
  async (arg, thunkAPI) => {
    const { extra: sdk } = thunkAPI;
    const { categoryKey = 'categoryLevel', categoryIds = [] } = arg || {};

    const results = await Promise.allSettled(
      categoryIds.map(id =>
        sdk.listings.query({
          [`pub_${categoryKey}1`]: id,
          perPage: 1,
          page: 1,
          minStock: 1,
          stockMode: 'match-undefined',
          'fields.listing': ['title'],
        })
      )
    );

    const counts = {};
    results.forEach((result, i) => {
      const totalItems = result.value?.data?.meta?.totalItems;
      if (result.status === 'fulfilled' && typeof totalItems === 'number') {
        counts[categoryIds[i]] = totalItems;
      } else if (result.status === 'rejected') {
        log.error(result.reason, 'landing-page-category-count-failed', { id: categoryIds[i] });
      }
    });
    return { counts };
  }
);

// ================ Slice ================ //

const initialState = {
  listingIds: [],
  featuredListingIds: [],
  queryInProgress: false,
  queryError: null,
  heroListingIds: [],
  heroQueryInProgress: false,
  // Published listings per top-level category id, e.g. { fashion: 12 }.
  categoryListingCounts: {},
  categoryCountsInProgress: false,
};

const landingPageSlice = createSlice({
  name: 'LandingPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchNewestListings.pending, state => {
        state.queryInProgress = true;
        state.queryError = null;
      })
      .addCase(fetchNewestListings.fulfilled, (state, action) => {
        state.queryInProgress = false;
        state.listingIds = action.payload.listingIds;
        state.featuredListingIds = action.payload.featuredListingIds;
      })
      .addCase(fetchNewestListings.rejected, (state, action) => {
        state.queryInProgress = false;
        state.queryError = action.payload;
      })
      .addCase(fetchHeroListings.pending, state => {
        state.heroQueryInProgress = true;
      })
      .addCase(fetchHeroListings.fulfilled, (state, action) => {
        state.heroQueryInProgress = false;
        state.heroListingIds = action.payload.listingIds;
      })
      .addCase(fetchHeroListings.rejected, state => {
        state.heroQueryInProgress = false;
        state.heroListingIds = [];
      })
      .addCase(fetchCategoryCounts.pending, state => {
        state.categoryCountsInProgress = true;
      })
      .addCase(fetchCategoryCounts.fulfilled, (state, action) => {
        state.categoryCountsInProgress = false;
        state.categoryListingCounts = action.payload.counts;
      })
      .addCase(fetchCategoryCounts.rejected, state => {
        state.categoryCountsInProgress = false;
      });
  },
});

export default landingPageSlice.reducer;

// ================ loadData ================ //

export const loadData = (params, search, config) => dispatch => {
  const listingImageConfig = config?.layout?.listingImage;
  const { key: categoryKey, categories = [] } = config?.categoryConfiguration || {};
  const categoryIds = categories.map(category => category.id);
  return Promise.all([
    dispatch(fetchNewestListings({ listingImageConfig, categoryKey })),
    dispatch(fetchHeroListings({ listingImageConfig })),
    dispatch(fetchCategoryCounts({ categoryKey, categoryIds })),
  ]);
};
